import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import Layout from '../../components/Layout';
import idCardsService from '../../services/idCardsService';
import studentsService from '../../services/studentsService';
import staffService from '../../services/staffService';
import securityService from '../../services/securityService';
import { Plus, DownloadSimple, QrCode, X, Camera, CheckCircle, Circle } from '@phosphor-icons/react';

const STATUSES = ['active', 'lost', 'revoked', 'reissued'];
const PERSON_TYPE_LABELS = { student: 'Student', staff: 'Staff', pickup_person: 'Pickup Person' };

function IDCardsPage() {
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [pickupPersons, setPickupPersons] = useState([]);
  const [personType, setPersonType] = useState('student');
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [qrCard, setQrCard] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [distributeDialog, setDistributeDialog] = useState(null); // card being distributed
  const [distributeNote, setDistributeNote] = useState('');
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [cardsData, studentsData, staffData, pickupData] = await Promise.all([
        idCardsService.listCards(),
        studentsService.listStudents().catch(() => []),
        staffService.listStaff().catch(() => []),
        securityService.listAllPickupPersons().catch(() => []),
      ]);
      setCards(Array.isArray(cardsData) ? cardsData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : studentsData?.items || studentsData?.students || []);
      setStaff(Array.isArray(staffData) ? staffData : staffData?.items || staffData?.staff || []);
      setPickupPersons(Array.isArray(pickupData) ? pickupData : []);
    } catch (error) {
      toast.error('Failed to load ID cards');
    } finally {
      setLoading(false);
    }
  };

  const peopleByType = { student: students, staff, pickup_person: pickupPersons };
  const people = peopleByType[personType] || [];

  const personLabel = (p) => {
    if (personType === 'pickup_person') {
      const bits = [p.relationship, p.student_name ? `for ${p.student_name}` : null].filter(Boolean);
      return `${p.name}${bits.length ? ` (${bits.join(', ')})` : ''}`;
    }
    return `${p.first_name} ${p.last_name}`;
  };

  const filteredPeople = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return people;
    return people.filter((p) => personLabel(p).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, filterText, personType]);

  const getPerson = (card) => {
    const list = peopleByType[card.person_type] || [];
    return list.find((p) => p.id === card.person_id);
  };

  const getPersonName = (card) => {
    const person = getPerson(card);
    if (!person) return card.person_id;
    return card.person_type === 'pickup_person' ? person.name : `${person.first_name} ${person.last_name}`;
  };

  const toggleSelected = (id) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  };

  const selectAllFiltered = () => setSelectedIds(filteredPeople.map((p) => p.id));
  const clearSelection = () => setSelectedIds([]);

  const handleGenerate = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one person');
      return;
    }
    setGenerating(true);
    try {
      const result = await idCardsService.bulkGenerate(personType, selectedIds, expiryDate || null);
      const createdCount = result.created_count ?? result.created?.length ?? 0;
      const skippedCount = result.skipped_already_has_card?.length || 0;
      if (createdCount === 1 && result.created?.[0]) {
        setQrCard(result.created[0]);
      }
      toast.success(
        `${createdCount} card${createdCount === 1 ? '' : 's'} generated` +
        (skippedCount ? `, ${skippedCount} already had an active card` : '')
      );
      setSelectedIds([]);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to generate ID card(s)');
    } finally {
      setGenerating(false);
    }
  };

  const handleStatusChange = async (card, status) => {
    try {
      await idCardsService.updateCard(card.id, { status });
      toast.success('Card status updated');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update card status');
    }
  };

  const handleDownloadPdf = async (card) => {
    setDownloadingId(card.id);
    try {
      const response = await idCardsService.downloadCardPdf(card.id);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `id_card_${card.card_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      toast.error(error.message || 'Failed to download ID card');
    } finally {
      setDownloadingId(null);
    }
  };

  const openDistributeDialog = (card) => {
    setDistributeDialog(card);
    setDistributeNote('');
  };

  const submitDistribute = async () => {
    if (!distributeDialog) return;
    try {
      await idCardsService.distributeCard(distributeDialog.id, distributeNote.trim() || null);
      toast.success('Card marked as distributed');
      setDistributeDialog(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update distribution status');
    }
  };

  const handlePhotoUpload = async (personId, file) => {
    if (!file) return;
    setUploadingPhotoFor(personId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await securityService.uploadPickupPersonPhoto(personId, formData);
      toast.success('Photo uploaded');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploadingPhotoFor(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading ID cards...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>ID Cards</h1>
            <p>Issue and manage identity cards for students, staff, and authorized pickup persons</p>
          </div>
        </div>

        <div className="form-card" style={{ marginBottom: 20, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Generate ID Cards</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Person Type</label>
              <select value={personType} onChange={(e) => { setPersonType(e.target.value); setSelectedIds([]); setFilterText(''); }}>
                <option value="student">Student</option>
                <option value="staff">Staff</option>
                <option value="pickup_person">Authorized Pickup Person</option>
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Expiry Date (optional)</label>
              <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: 220 }}>
              <label>Filter</label>
              <input type="text" placeholder="Search by name..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
            </div>
            <button className="btn btn-outline" type="button" onClick={selectAllFiltered}>Select all ({filteredPeople.length})</button>
            <button className="btn btn-outline" type="button" onClick={clearSelection} disabled={selectedIds.length === 0}>Clear</button>
            <button className="btn btn-primary" onClick={handleGenerate} disabled={generating || selectedIds.length === 0}>
              <Plus size={18} />
              {generating ? 'Generating...' : `Generate ${selectedIds.length > 1 ? `(${selectedIds.length})` : 'Card'}`}
            </button>
          </div>

          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
            {filteredPeople.length === 0 ? (
              <div style={{ padding: 16, color: '#6b7280' }}>No {PERSON_TYPE_LABELS[personType].toLowerCase()}s found.</div>
            ) : (
              filteredPeople.map((p) => {
                const checked = selectedIds.includes(p.id);
                return (
                  <div
                    key={p.id}
                    onClick={() => toggleSelected(p.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                      borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                      background: checked ? '#eff6ff' : 'transparent',
                    }}
                  >
                    {checked ? <CheckCircle size={18} weight="fill" color="#3b82f6" /> : <Circle size={18} color="#94a3b8" />}
                    {personType === 'pickup_person' && p.photo_url && (
                      <img src={p.photo_url} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                    )}
                    <span style={{ flex: 1 }}>{personLabel(p)}</span>
                    {personType === 'pickup_person' && (
                      <label
                        className="btn-icon"
                        title="Upload photo"
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: 'pointer' }}
                      >
                        {uploadingPhotoFor === p.id ? '…' : <Camera size={16} />}
                        <input
                          type="file"
                          accept="image/jpeg,image/png"
                          style={{ display: 'none' }}
                          onChange={(e) => handlePhotoUpload(p.id, e.target.files?.[0])}
                        />
                      </label>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="table-container">
          {cards.length === 0 ? (
            <div className="empty-state">
              <p>No ID cards issued yet</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Card #</th>
                  <th>Person</th>
                  <th>Type</th>
                  <th>Issue Date</th>
                  <th>Status</th>
                  <th>Distribution</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((card) => (
                  <tr key={card.id}>
                    <td>{card.card_number}</td>
                    <td>{getPersonName(card)}</td>
                    <td className="text-capitalize">{PERSON_TYPE_LABELS[card.person_type] || card.person_type}</td>
                    <td>{card.issue_date}</td>
                    <td>
                      <select
                        value={card.status}
                        onChange={(e) => handleStatusChange(card, e.target.value)}
                        className="filter-select"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {card.distributed_at ? (
                        <span
                          title={card.distributed_note || ''}
                          style={{ background: '#dcfce7', color: '#166534', borderRadius: 6, padding: '3px 8px', fontSize: 12, whiteSpace: 'nowrap' }}
                        >
                          Distributed {new Date(card.distributed_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <button className="btn btn-sm btn-outline" onClick={() => openDistributeDialog(card)}>
                          Mark Distributed
                        </button>
                      )}
                    </td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="Show QR" onClick={() => setQrCard(card)}>
                        <QrCode size={18} />
                      </button>
                      <button
                        className="btn-icon btn-primary"
                        title="Download PDF"
                        onClick={() => handleDownloadPdf(card)}
                        disabled={downloadingId === card.id}
                      >
                        <DownloadSimple size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {qrCard && (
        <div className="modal-overlay" onClick={() => setQrCard(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 340, textAlign: 'center' }}>
            <div className="modal-header">
              <h2>ID Card QR</h2>
              <button type="button" className="btn-close" onClick={() => setQrCard(null)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '8px 0 20px' }}>
              <p style={{ margin: '0 0 16px', fontWeight: 600 }}>{getPersonName(qrCard)}</p>
              <div style={{ background: '#fff', padding: 16, borderRadius: 12, display: 'inline-block' }}>
                <QRCode value={qrCard.qr_payload} size={180} level="H" />
              </div>
              <p style={{ marginTop: 12, fontFamily: 'monospace', fontSize: 14 }}>{qrCard.card_number}</p>
            </div>
          </div>
        </div>
      )}

      {distributeDialog && (
        <div className="modal-overlay" onClick={() => setDistributeDialog(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h2>Mark Card Distributed</h2>
              <button type="button" className="btn-close" onClick={() => setDistributeDialog(null)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '8px 0 4px' }}>
              <p style={{ margin: '0 0 12px' }}>{distributeDialog.card_number} — {getPersonName(distributeDialog)}</p>
              <div className="form-group">
                <label>Note (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. collected in person"
                  value={distributeNote}
                  onChange={(e) => setDistributeNote(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button className="btn btn-outline" onClick={() => setDistributeDialog(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={submitDistribute}>Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default IDCardsPage;
